import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import {
  Menu, LayoutGrid, Pencil, Plus, ArrowUp, ArrowDown, Image as ImageIcon, Upload,
  ExternalLink, Instagram, Facebook, Youtube, Music2, Globe as GlobeIcon, FileText, PanelBottom,
} from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Textarea, Select } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { InlineConfirm } from '@/components/ui/InlineConfirm';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { uploadSiteImage } from '@/lib/siteImageApi';
import { SITE_IMAGE_KEYS } from '@/constants/siteImageKeys';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

type Locale = 'en' | 'ko' | 'bn';
type LabelMap = Record<Locale, string>;
type Tab = 'header' | 'footer';

const emptyLabel = (): LabelMap => ({ en: '', ko: '', bn: '' });
const asLabel = (raw: unknown): LabelMap => {
  const r = (raw ?? {}) as Partial<LabelMap>;
  return { en: r.en ?? '', ko: r.ko ?? '', bn: r.bn ?? '' };
};

interface NavLinkRow { id: string; label: LabelMap; href: string; is_external: boolean; sort_order: number; }
interface FooterColumnRow { id: string; title: LabelMap; sort_order: number; }
interface FooterLinkRow { id: string; column_id: string; label: LabelMap; href: string; is_external: boolean; sort_order: number; }
interface SocialLinkRow { id: string; platform: string; url: string; sort_order: number; }
interface ContentRow { key: string; locale: string; value: string; }

// Standalone footer text that isn't part of the link-structure tables
// above — still lives in rxk_site_content, edited here so everything
// header/footer is in one place instead of two. (Header has no leftover
// standalone text: "Sign In" is deliberately not admin-editable — see
// adminPages.ts — and "Shop Now" is a shared string already editable
// under Settings.)
const DISCLOSURE_TEXT_KEYS: { key: string; label: string }[] = [
  { key: 'footer.brandDesc', label: 'Brand Description' },
  { key: 'footer.hotlineLabel', label: 'Hotline Label' },
  { key: 'footer.hotline', label: 'Hotline Number' },
  { key: 'footer.marketingAuth', label: 'Marketing Authorization Holder' },
  { key: 'footer.manufacturer', label: 'Manufacturer' },
  { key: 'footer.bdOperations', label: 'Bangladesh Operations' },
  { key: 'footer.registeredAddress', label: 'Registered Address' },
  { key: 'footer.tradeLicense', label: 'Trade License' },
];

// The bottom bar's Privacy Policy / Terms of Service labels are their own
// rxk_site_content rows, independent of the same-named links inside the
// Support footer column (rxk_footer_links) — editing one does not change
// the other. Simpler and safer than making the bottom bar reach into a
// specific column's link by href to find "the" label.
const BOTTOM_BAR_TEXT_KEYS: { key: string; label: string }[] = [
  { key: 'footer.copyright', label: 'Copyright Line' },
  { key: 'footer.dgda', label: 'DGDA Registration Line' },
  { key: 'footer.privacyPolicy', label: 'Privacy Policy Label' },
  { key: 'footer.termsOfService', label: 'Terms of Service Label' },
  { key: 'footer.parentCompany', label: 'Parent Company Label' },
];

const SOCIAL_PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'facebook', label: 'Facebook', icon: Facebook },
  { value: 'youtube', label: 'YouTube', icon: Youtube },
  { value: 'tiktok', label: 'TikTok', icon: Music2 },
  { value: 'custom', label: 'Other', icon: GlobeIcon },
];

function platformIcon(platform: string) {
  const found = SOCIAL_PLATFORMS.find((p) => p.value === platform);
  const Icon = found?.icon ?? GlobeIcon;
  return <Icon className="w-4 h-4" />;
}

export default function AdminHeaderFooterPage() {
  const { push } = useToast();
  const [tab, setTab] = useState<Tab>('header');
  const [loading, setLoading] = useState(true);

  const [navLinks, setNavLinks] = useState<NavLinkRow[]>([]);
  const [footerColumns, setFooterColumns] = useState<FooterColumnRow[]>([]);
  const [footerLinks, setFooterLinks] = useState<FooterLinkRow[]>([]);
  const [socialLinks, setSocialLinks] = useState<SocialLinkRow[]>([]);
  const [contentRows, setContentRows] = useState<ContentRow[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingImageKeyRef = useRef<string | null>(null);

  // ── Nav link modal ──
  const [navModal, setNavModal] = useState<{ id: string | null; label: LabelMap; href: string; is_external: boolean } | null>(null);
  // ── Footer link modal ──
  const [linkModal, setLinkModal] = useState<{ id: string | null; column_id: string; label: LabelMap; href: string; is_external: boolean } | null>(null);
  // ── Footer column title modal ──
  const [columnModal, setColumnModal] = useState<{ id: string | null; title: LabelMap } | null>(null);
  // ── Social link modal ──
  const [socialModal, setSocialModal] = useState<{ id: string | null; platform: string; url: string } | null>(null);
  // ── Standalone text modal (rxk_site_content) ──
  const [textModal, setTextModal] = useState<{ key: string; label: string; en: string; ko: string; bn: string } | null>(null);

  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [navRes, colRes, linkRes, socialRes, contentRes, imagesRes] = await Promise.all([
      supabase.from('rxk_nav_links').select('id, label, href, is_external, sort_order').order('sort_order'),
      supabase.from('rxk_footer_columns').select('id, title, sort_order').order('sort_order'),
      supabase.from('rxk_footer_links').select('id, column_id, label, href, is_external, sort_order').order('sort_order'),
      supabase.from('rxk_social_links').select('id, platform, url, sort_order').order('sort_order'),
      supabase.from('rxk_site_content').select('key, locale, value'),
      supabase.from('rxk_site_images').select('key, url'),
    ]);
    setNavLinks(((navRes.data as { id: string; label: unknown; href: string; is_external: boolean; sort_order: number }[]) ?? []).map((r) => ({ ...r, label: asLabel(r.label) })));
    setFooterColumns(((colRes.data as { id: string; title: unknown; sort_order: number }[]) ?? []).map((r) => ({ ...r, title: asLabel(r.title) })));
    setFooterLinks(((linkRes.data as { id: string; column_id: string; label: unknown; href: string; is_external: boolean; sort_order: number }[]) ?? []).map((r) => ({ ...r, label: asLabel(r.label) })));
    setSocialLinks((socialRes.data as SocialLinkRow[]) ?? []);
    setContentRows((contentRes.data as ContentRow[]) ?? []);
    const images: Record<string, string> = {};
    for (const row of (imagesRes.data as { key: string; url: string }[]) ?? []) images[row.key] = row.url;
    setImageUrls(images);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const contentValue = (key: string, locale: Locale) => contentRows.find((r) => r.key === key && r.locale === locale)?.value ?? '';
  // rxk_site_images only has a row once someone actually replaces an
  // image — most keys have never been touched, so imageUrls[key] is
  // routinely undefined and must fall back to the same bundled default
  // useSiteImage() uses on the live site, or every preview here renders
  // a broken <img>.
  const imageFor = (key: string) => imageUrls[key] ?? SITE_IMAGE_KEYS.find((d) => d.key === key)?.fallbackUrl ?? '';
  const sizeHint = (key: string) => SITE_IMAGE_KEYS.find((d) => d.key === key)?.recommendedSize ?? '';

  // ── Image upload ──
  const triggerImageUpload = (key: string) => {
    pendingImageKeyRef.current = key;
    fileInputRef.current?.click();
  };
  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const key = pendingImageKeyRef.current;
    e.target.value = '';
    if (!file || !key) return;
    setUploadingKey(key);
    try {
      await uploadSiteImage(key, file);
      push('success', 'Image updated — live on the site immediately');
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingKey(null);
    }
  };

  // ── Standalone text (rxk_site_content) ──
  const openTextModal = (key: string, label: string) => {
    setTextModal({ key, label, en: contentValue(key, 'en'), ko: contentValue(key, 'ko'), bn: contentValue(key, 'bn') });
  };
  const saveTextModal = async (e: FormEvent) => {
    e.preventDefault();
    if (!textModal) return;
    setBusy(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const upserts = (['en', 'ko', 'bn'] as const).map((locale) => ({
        key: textModal.key,
        locale,
        value: textModal[locale],
        updated_by: user?.id ?? null,
      }));
      const { error } = await supabase.from('rxk_site_content').upsert(upserts, { onConflict: 'key,locale' });
      if (error) throw error;
      push('success', 'Saved — live on the site immediately');
      setTextModal(null);
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  // ── Nav links CRUD ──
  const saveNavModal = async (e: FormEvent) => {
    e.preventDefault();
    if (!navModal) return;
    setBusy(true);
    try {
      if (navModal.id) {
        const { error } = await supabase.from('rxk_nav_links').update({ label: navModal.label, href: navModal.href, is_external: navModal.is_external }).eq('id', navModal.id);
        if (error) throw error;
      } else {
        const nextOrder = navLinks.length > 0 ? Math.max(...navLinks.map((l) => l.sort_order)) + 1 : 1;
        const { error } = await supabase.from('rxk_nav_links').insert({ label: navModal.label, href: navModal.href, is_external: navModal.is_external, sort_order: nextOrder });
        if (error) throw error;
      }
      push('success', 'Nav link saved — live on the site immediately');
      setNavModal(null);
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };
  const deleteNavLink = async (id: string) => {
    const { error } = await supabase.from('rxk_nav_links').delete().eq('id', id);
    if (error) { push('error', error.message); return; }
    push('success', 'Nav link removed');
    load();
  };
  const moveNavLink = async (index: number, direction: -1 | 1) => {
    const target = navLinks[index + direction];
    const current = navLinks[index];
    if (!target || !current) return;
    await supabase.from('rxk_nav_links').update({ sort_order: target.sort_order }).eq('id', current.id);
    await supabase.from('rxk_nav_links').update({ sort_order: current.sort_order }).eq('id', target.id);
    load();
  };

  // ── Footer columns CRUD ──
  const saveColumnModal = async (e: FormEvent) => {
    e.preventDefault();
    if (!columnModal) return;
    setBusy(true);
    try {
      if (columnModal.id) {
        const { error } = await supabase.from('rxk_footer_columns').update({ title: columnModal.title }).eq('id', columnModal.id);
        if (error) throw error;
      } else {
        const nextOrder = footerColumns.length > 0 ? Math.max(...footerColumns.map((c) => c.sort_order)) + 1 : 1;
        const { error } = await supabase.from('rxk_footer_columns').insert({ title: columnModal.title, sort_order: nextOrder });
        if (error) throw error;
      }
      push('success', 'Column saved — live on the site immediately');
      setColumnModal(null);
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };
  const deleteColumn = async (id: string) => {
    const { error } = await supabase.from('rxk_footer_columns').delete().eq('id', id);
    if (error) { push('error', error.message); return; }
    push('success', 'Column removed (its links went with it)');
    load();
  };
  const moveColumn = async (index: number, direction: -1 | 1) => {
    const target = footerColumns[index + direction];
    const current = footerColumns[index];
    if (!target || !current) return;
    await supabase.from('rxk_footer_columns').update({ sort_order: target.sort_order }).eq('id', current.id);
    await supabase.from('rxk_footer_columns').update({ sort_order: current.sort_order }).eq('id', target.id);
    load();
  };

  // ── Footer links CRUD ──
  const saveLinkModal = async (e: FormEvent) => {
    e.preventDefault();
    if (!linkModal) return;
    setBusy(true);
    try {
      if (linkModal.id) {
        const { error } = await supabase.from('rxk_footer_links').update({ label: linkModal.label, href: linkModal.href, is_external: linkModal.is_external }).eq('id', linkModal.id);
        if (error) throw error;
      } else {
        const siblings = footerLinks.filter((l) => l.column_id === linkModal.column_id);
        const nextOrder = siblings.length > 0 ? Math.max(...siblings.map((l) => l.sort_order)) + 1 : 1;
        const { error } = await supabase.from('rxk_footer_links').insert({ column_id: linkModal.column_id, label: linkModal.label, href: linkModal.href, is_external: linkModal.is_external, sort_order: nextOrder });
        if (error) throw error;
      }
      push('success', 'Link saved — live on the site immediately');
      setLinkModal(null);
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };
  const deleteFooterLink = async (id: string) => {
    const { error } = await supabase.from('rxk_footer_links').delete().eq('id', id);
    if (error) { push('error', error.message); return; }
    push('success', 'Link removed');
    load();
  };
  const moveFooterLink = async (columnId: string, index: number, direction: -1 | 1) => {
    const siblings = footerLinks.filter((l) => l.column_id === columnId).sort((a, b) => a.sort_order - b.sort_order);
    const target = siblings[index + direction];
    const current = siblings[index];
    if (!target || !current) return;
    await supabase.from('rxk_footer_links').update({ sort_order: target.sort_order }).eq('id', current.id);
    await supabase.from('rxk_footer_links').update({ sort_order: current.sort_order }).eq('id', target.id);
    load();
  };

  // ── Social links CRUD ──
  const saveSocialModal = async (e: FormEvent) => {
    e.preventDefault();
    if (!socialModal) return;
    setBusy(true);
    try {
      if (socialModal.id) {
        const { error } = await supabase.from('rxk_social_links').update({ platform: socialModal.platform, url: socialModal.url }).eq('id', socialModal.id);
        if (error) throw error;
      } else {
        const nextOrder = socialLinks.length > 0 ? Math.max(...socialLinks.map((l) => l.sort_order)) + 1 : 1;
        const { error } = await supabase.from('rxk_social_links').insert({ platform: socialModal.platform, url: socialModal.url, sort_order: nextOrder });
        if (error) throw error;
      }
      push('success', 'Social link saved — live on the site immediately');
      setSocialModal(null);
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };
  const deleteSocialLink = async (id: string) => {
    const { error } = await supabase.from('rxk_social_links').delete().eq('id', id);
    if (error) { push('error', error.message); return; }
    push('success', 'Social link removed');
    load();
  };

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div>
          <h1 className="text-[18px] font-bold text-ink">Header &amp; Footer</h1>
          <p className="text-[12px] text-ink-faint mt-0.5">Nav links, footer columns, social links, logos, and the bottom bar</p>
        </div>

        <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleImageFileChange} />

        <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
          <button
            onClick={() => setTab('header')}
            className={`flex items-center gap-1.5 text-[12.5px] font-semibold px-3.5 py-2 rounded-xl cursor-pointer transition-colors ${
              tab === 'header' ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'
            }`}
          >
            <Menu className="w-3.5 h-3.5" /> Header
          </button>
          <button
            onClick={() => setTab('footer')}
            className={`flex items-center gap-1.5 text-[12.5px] font-semibold px-3.5 py-2 rounded-xl cursor-pointer transition-colors ${
              tab === 'footer' ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Footer
          </button>
        </div>

        {loading ? (
          <Spinner />
        ) : tab === 'header' ? (
          <div className="space-y-5">
            <Card>
              <CardHeader title="Logo" icon={<ImageIcon className="w-4 h-4" />} />
              <CardBody>
                <div className="border border-border-subtle rounded-lg p-3 flex flex-col gap-2 w-fit">
                  <div className="h-16 w-48 rounded-lg bg-surface-muted flex items-center justify-center overflow-hidden">
                    <img src={imageFor('nav.logo')} alt="Header logo" className="max-h-full max-w-full object-contain" />
                  </div>
                  <p className="text-[10px] text-ink-faint leading-snug max-w-48">{sizeHint('nav.logo')}</p>
                  <Button size="sm" variant="secondary" onClick={() => triggerImageUpload('nav.logo')} disabled={uploadingKey === 'nav.logo'}>
                    <Upload className="w-3.5 h-3.5" /> {uploadingKey === 'nav.logo' ? 'Uploading…' : 'Replace'}
                  </Button>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Nav Links"
                subtitle={`${navLinks.length} link(s)`}
                icon={<Menu className="w-4 h-4" />}
                action={
                  <Button size="sm" onClick={() => setNavModal({ id: null, label: emptyLabel(), href: '/', is_external: false })}>
                    <Plus className="w-3.5 h-3.5" /> Add Link
                  </Button>
                }
              />
              <CardBody className="space-y-4">
                {/* Live preview — exact colors/spacing of the real header
                    (nav links + the Sign In / Shop Now buttons alongside
                    them), so an edit's effect is obvious before it's even
                    saved. */}
                <div className="rounded-xl bg-white border border-[#E8E8E6] px-6 py-4 flex items-center justify-between gap-4 overflow-x-auto shadow-sm">
                  <div className="flex items-center gap-7 flex-shrink-0">
                    {navLinks.length === 0 ? (
                      <span className="text-[11px] text-ink-faint">No links yet</span>
                    ) : (
                      navLinks.map((link) => (
                        <span key={link.id} className="text-[11px] tracking-[0.15em] uppercase font-normal text-[#2A2A2A] whitespace-nowrap">
                          {link.label.en || '(no label)'}
                        </span>
                      ))
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-xs tracking-wider uppercase text-[#2A2A2A] whitespace-nowrap">Sign In</span>
                    <span className="px-5 py-2 bg-[#7B1113] text-white text-[11px] tracking-[0.12em] uppercase rounded-full whitespace-nowrap">
                      {contentValue('common.shopNow', 'en') || 'Shop Now'}
                    </span>
                  </div>
                </div>
                <div className="space-y-1">
                {navLinks.length === 0 ? (
                  <EmptyState title="No nav links yet" />
                ) : (
                  navLinks.map((link, i) => (
                    <div key={link.id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-surface-muted">
                      <div className="flex flex-col gap-0.5 flex-shrink-0">
                        <button disabled={i === 0} onClick={() => moveNavLink(i, -1)} className="w-5 h-5 flex items-center justify-center rounded text-ink-faint hover:text-ink disabled:opacity-25 cursor-pointer disabled:cursor-not-allowed">
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button disabled={i === navLinks.length - 1} onClick={() => moveNavLink(i, 1)} className="w-5 h-5 flex items-center justify-center rounded text-ink-faint hover:text-ink disabled:opacity-25 cursor-pointer disabled:cursor-not-allowed">
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12.5px] font-semibold text-ink truncate">{link.label.en || '(no label)'}</p>
                        <p className="text-[11px] text-ink-faint truncate flex items-center gap-1">
                          {link.href} {link.is_external && <ExternalLink className="w-3 h-3" />}
                        </p>
                      </div>
                      <Button size="sm" variant="secondary" onClick={() => setNavModal({ id: link.id, label: link.label, href: link.href, is_external: link.is_external })}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <InlineConfirm onConfirm={() => deleteNavLink(link.id)} label="" ariaLabel="Delete link" />
                    </div>
                  ))
                )}
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Header Buttons" icon={<Menu className="w-4 h-4" />} />
              <CardBody className="space-y-1">
                <button
                  onClick={() => openTextModal('common.shopNow', 'Shop Now Button')}
                  className="w-full flex items-center justify-between gap-3 px-2 py-2.5 rounded-lg hover:bg-surface-muted transition-colors duration-150 text-left cursor-pointer"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-ink truncate">Shop Now Button</p>
                    <p className="text-[12px] text-ink-muted truncate">{contentValue('common.shopNow', 'en')}</p>
                  </div>
                  <Pencil className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
                </button>
                <div className="flex items-center justify-between gap-3 px-2 py-2.5 rounded-lg opacity-60">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-ink truncate">Sign In Link</p>
                    <p className="text-[11px] text-ink-faint">Not editable here — shared with the Login page itself</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Card>
                <CardHeader title="Brand Logo" icon={<ImageIcon className="w-4 h-4" />} />
                <CardBody>
                  <div className="border border-border-subtle rounded-lg p-3 flex flex-col gap-2 w-fit">
                    <div className="h-16 w-48 rounded-lg bg-[#1A1A1A] flex items-center justify-center overflow-hidden">
                      <img src={imageFor('footer.logo')} alt="Footer logo" className="max-h-full max-w-full object-contain" />
                    </div>
                    <p className="text-[10px] text-ink-faint leading-snug max-w-48">{sizeHint('footer.logo')}</p>
                    <Button size="sm" variant="secondary" onClick={() => triggerImageUpload('footer.logo')} disabled={uploadingKey === 'footer.logo'}>
                      <Upload className="w-3.5 h-3.5" /> {uploadingKey === 'footer.logo' ? 'Uploading…' : 'Replace'}
                    </Button>
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardHeader title="Parent Company Logo" icon={<ImageIcon className="w-4 h-4" />} />
                <CardBody>
                  <div className="border border-border-subtle rounded-lg p-3 flex flex-col gap-2 w-fit">
                    <div className="h-16 w-48 rounded-lg bg-[#1A1A1A] flex items-center justify-center overflow-hidden">
                      <img src={imageFor('footer.parentLogo')} alt="Parent logo" className="max-h-full max-w-full object-contain" />
                    </div>
                    <p className="text-[10px] text-ink-faint leading-snug max-w-48">{sizeHint('footer.parentLogo')}</p>
                    <Button size="sm" variant="secondary" onClick={() => triggerImageUpload('footer.parentLogo')} disabled={uploadingKey === 'footer.parentLogo'}>
                      <Upload className="w-3.5 h-3.5" /> {uploadingKey === 'footer.parentLogo' ? 'Uploading…' : 'Replace'}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>

            <Card>
              <CardHeader title="Payment Methods Banner" icon={<ImageIcon className="w-4 h-4" />} />
              <CardBody>
                <div className="border border-border-subtle rounded-lg p-3 flex flex-col gap-2 w-fit">
                  <div className="h-16 w-72 rounded-lg bg-surface-muted flex items-center justify-center overflow-hidden">
                    <img src={imageFor('footer.paymentBanner')} alt="Payment methods" className="max-h-full max-w-full object-contain" />
                  </div>
                  <p className="text-[10px] text-ink-faint leading-snug max-w-72">{sizeHint('footer.paymentBanner')}</p>
                  <Button size="sm" variant="secondary" onClick={() => triggerImageUpload('footer.paymentBanner')} disabled={uploadingKey === 'footer.paymentBanner'}>
                    <Upload className="w-3.5 h-3.5" /> {uploadingKey === 'footer.paymentBanner' ? 'Uploading…' : 'Replace'}
                  </Button>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Social Links"
                subtitle={`${socialLinks.length} link(s)`}
                icon={<Instagram className="w-4 h-4" />}
                action={
                  <Button size="sm" onClick={() => setSocialModal({ id: null, platform: 'instagram', url: '' })}>
                    <Plus className="w-3.5 h-3.5" /> Add Social Link
                  </Button>
                }
              />
              <CardBody className="space-y-1">
                {socialLinks.length === 0 ? (
                  <EmptyState title="No social links yet" />
                ) : (
                  socialLinks.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-surface-muted">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-surface-muted flex-shrink-0">{platformIcon(s.platform)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12.5px] font-semibold text-ink capitalize">{s.platform}</p>
                        <p className="text-[11px] text-ink-faint truncate">{s.url}</p>
                      </div>
                      <Button size="sm" variant="secondary" onClick={() => setSocialModal({ id: s.id, platform: s.platform, url: s.url })}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <InlineConfirm onConfirm={() => deleteSocialLink(s.id)} label="" ariaLabel="Delete social link" />
                    </div>
                  ))
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Brand Description & Legal Disclosure" subtitle="Shown above the bottom bar" icon={<FileText className="w-4 h-4" />} />
              <CardBody className="space-y-1">
                {DISCLOSURE_TEXT_KEYS.map((k) => (
                  <button
                    key={k.key}
                    onClick={() => openTextModal(k.key, k.label)}
                    className="w-full flex items-center justify-between gap-3 px-2 py-2.5 rounded-lg hover:bg-surface-muted transition-colors duration-150 text-left cursor-pointer"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-ink truncate">{k.label}</p>
                      <p className="text-[12px] text-ink-muted truncate">{contentValue(k.key, 'en')}</p>
                    </div>
                    <Pencil className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
                  </button>
                ))}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Bottom Bar" subtitle="The last line of the footer" icon={<PanelBottom className="w-4 h-4" />} />
              <CardBody className="space-y-4">
                {/* Live preview — the real footer's dark background with brighter
                    text than the actual site (which intentionally uses very
                    faint disclaimer-style text) so it's actually legible here. */}
                <div className="rounded-xl bg-[#1A1A1A] px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 overflow-x-auto">
                  <p className="text-[11px] text-white/70 font-light whitespace-nowrap">{contentValue('footer.copyright', 'en') || '(empty)'}</p>
                  <div className="flex items-center gap-3 flex-wrap justify-center">
                    <span className="text-[11px] text-white/70 font-light whitespace-nowrap">{contentValue('footer.dgda', 'en') || '(empty)'}</span>
                    <span className="text-white/25">|</span>
                    <span className="text-[11px] text-white/70 font-light whitespace-nowrap">{contentValue('footer.privacyPolicy', 'en') || '(empty)'}</span>
                    <span className="text-white/25">|</span>
                    <span className="text-[11px] text-white/70 font-light whitespace-nowrap">{contentValue('footer.termsOfService', 'en') || '(empty)'}</span>
                    <span className="text-white/25">|</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-white/70 font-light whitespace-nowrap">{contentValue('footer.parentCompany', 'en') || '(empty)'}</span>
                      <img src={imageFor('footer.parentLogo')} alt="Parent logo" className="h-5 w-auto opacity-90 bg-white/10 rounded" />
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  {BOTTOM_BAR_TEXT_KEYS.map((k) => (
                    <button
                      key={k.key}
                      onClick={() => openTextModal(k.key, k.label)}
                      className="w-full flex items-center justify-between gap-3 px-2 py-2.5 rounded-lg hover:bg-surface-muted transition-colors duration-150 text-left cursor-pointer"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-ink truncate">{k.label}</p>
                        <p className="text-[12px] text-ink-muted truncate">{contentValue(k.key, 'en')}</p>
                      </div>
                      <Pencil className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </CardBody>
            </Card>

            <div className="flex items-center justify-between">
              <p className="text-[13px] font-bold text-ink">Footer Columns</p>
              <Button size="sm" onClick={() => setColumnModal({ id: null, title: emptyLabel() })}>
                <Plus className="w-3.5 h-3.5" /> Add Column
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {footerColumns.map((col, colIndex) => {
                const links = footerLinks.filter((l) => l.column_id === col.id).sort((a, b) => a.sort_order - b.sort_order);
                return (
                  <Card key={col.id}>
                    <CardHeader
                      title={col.title.en || '(untitled column)'}
                      icon={<LayoutGrid className="w-4 h-4" />}
                      action={
                        <div className="flex items-center gap-1">
                          <button disabled={colIndex === 0} onClick={() => moveColumn(colIndex, -1)} className="w-6 h-6 flex items-center justify-center rounded text-ink-faint hover:text-ink disabled:opacity-25 cursor-pointer disabled:cursor-not-allowed">
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button disabled={colIndex === footerColumns.length - 1} onClick={() => moveColumn(colIndex, 1)} className="w-6 h-6 flex items-center justify-center rounded text-ink-faint hover:text-ink disabled:opacity-25 cursor-pointer disabled:cursor-not-allowed">
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                          <Button size="sm" variant="secondary" onClick={() => setColumnModal({ id: col.id, title: col.title })}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      }
                    />
                    <CardBody className="space-y-1">
                      {links.map((link, i) => (
                        <div key={link.id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-surface-muted">
                          <div className="flex flex-col gap-0.5 flex-shrink-0">
                            <button disabled={i === 0} onClick={() => moveFooterLink(col.id, i, -1)} className="w-5 h-5 flex items-center justify-center rounded text-ink-faint hover:text-ink disabled:opacity-25 cursor-pointer disabled:cursor-not-allowed">
                              <ArrowUp className="w-3 h-3" />
                            </button>
                            <button disabled={i === links.length - 1} onClick={() => moveFooterLink(col.id, i, 1)} className="w-5 h-5 flex items-center justify-center rounded text-ink-faint hover:text-ink disabled:opacity-25 cursor-pointer disabled:cursor-not-allowed">
                              <ArrowDown className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-ink truncate">{link.label.en || '(no label)'}</p>
                            <p className="text-[10.5px] text-ink-faint truncate">{link.href}</p>
                          </div>
                          <Button size="sm" variant="secondary" onClick={() => setLinkModal({ id: link.id, column_id: col.id, label: link.label, href: link.href, is_external: link.is_external })}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <InlineConfirm onConfirm={() => deleteFooterLink(link.id)} label="" ariaLabel="Delete link" />
                        </div>
                      ))}
                      <Button size="sm" variant="secondary" className="w-full mt-1" onClick={() => setLinkModal({ id: null, column_id: col.id, label: emptyLabel(), href: '/', is_external: false })}>
                        <Plus className="w-3.5 h-3.5" /> Add Link
                      </Button>
                      <InlineConfirm onConfirm={() => deleteColumn(col.id)} label="Delete Column" confirmLabel="Delete column + its links?" />
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Nav link modal */}
      <Modal
        open={!!navModal}
        onClose={() => setNavModal(null)}
        title={navModal?.id ? 'Edit Nav Link' : 'Add Nav Link'}
        icon={<Menu className="w-3.5 h-3.5" />}
        busy={busy}
        footer={
          <>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setNavModal(null)} disabled={busy}>Cancel</Button>
            <Button type="submit" form="nav-link-form" className="flex-1" loading={busy}>Save</Button>
          </>
        }
      >
        {navModal && (
          <form id="nav-link-form" onSubmit={saveNavModal} className="space-y-3.5">
            <Field label="Label (English)">
              <Input required value={navModal.label.en} onChange={(e) => setNavModal({ ...navModal, label: { ...navModal.label, en: e.target.value } })} />
            </Field>
            <Field label="라벨 (한국어)">
              <Input required value={navModal.label.ko} onChange={(e) => setNavModal({ ...navModal, label: { ...navModal.label, ko: e.target.value } })} />
            </Field>
            <Field label="লেবেল (বাংলা)">
              <Input required value={navModal.label.bn} onChange={(e) => setNavModal({ ...navModal, label: { ...navModal.label, bn: e.target.value } })} />
            </Field>
            <Field label="Link (e.g. /products or https://...)">
              <Input required value={navModal.href} onChange={(e) => setNavModal({ ...navModal, href: e.target.value })} />
            </Field>
            <label className="flex items-center gap-2 cursor-pointer w-fit select-none">
              <input type="checkbox" checked={navModal.is_external} onChange={(e) => setNavModal({ ...navModal, is_external: e.target.checked })} className="w-4 h-4 accent-primary-800 cursor-pointer" />
              <span className="text-[12.5px] font-semibold text-ink">Opens in a new tab</span>
            </label>
          </form>
        )}
      </Modal>

      {/* Footer link modal */}
      <Modal
        open={!!linkModal}
        onClose={() => setLinkModal(null)}
        title={linkModal?.id ? 'Edit Footer Link' : 'Add Footer Link'}
        icon={<LayoutGrid className="w-3.5 h-3.5" />}
        busy={busy}
        footer={
          <>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setLinkModal(null)} disabled={busy}>Cancel</Button>
            <Button type="submit" form="footer-link-form" className="flex-1" loading={busy}>Save</Button>
          </>
        }
      >
        {linkModal && (
          <form id="footer-link-form" onSubmit={saveLinkModal} className="space-y-3.5">
            <Field label="Label (English)">
              <Input required value={linkModal.label.en} onChange={(e) => setLinkModal({ ...linkModal, label: { ...linkModal.label, en: e.target.value } })} />
            </Field>
            <Field label="라벨 (한국어)">
              <Input required value={linkModal.label.ko} onChange={(e) => setLinkModal({ ...linkModal, label: { ...linkModal.label, ko: e.target.value } })} />
            </Field>
            <Field label="লেবেল (বাংলা)">
              <Input required value={linkModal.label.bn} onChange={(e) => setLinkModal({ ...linkModal, label: { ...linkModal.label, bn: e.target.value } })} />
            </Field>
            <Field label="Link (e.g. /faq or https://...)">
              <Input required value={linkModal.href} onChange={(e) => setLinkModal({ ...linkModal, href: e.target.value })} />
            </Field>
            <label className="flex items-center gap-2 cursor-pointer w-fit select-none">
              <input type="checkbox" checked={linkModal.is_external} onChange={(e) => setLinkModal({ ...linkModal, is_external: e.target.checked })} className="w-4 h-4 accent-primary-800 cursor-pointer" />
              <span className="text-[12.5px] font-semibold text-ink">Opens in a new tab</span>
            </label>
          </form>
        )}
      </Modal>

      {/* Footer column title modal */}
      <Modal
        open={!!columnModal}
        onClose={() => setColumnModal(null)}
        title={columnModal?.id ? 'Edit Column Title' : 'Add Column'}
        icon={<LayoutGrid className="w-3.5 h-3.5" />}
        busy={busy}
        footer={
          <>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setColumnModal(null)} disabled={busy}>Cancel</Button>
            <Button type="submit" form="footer-column-form" className="flex-1" loading={busy}>Save</Button>
          </>
        }
      >
        {columnModal && (
          <form id="footer-column-form" onSubmit={saveColumnModal} className="space-y-3.5">
            <Field label="Title (English)">
              <Input required value={columnModal.title.en} onChange={(e) => setColumnModal({ ...columnModal, title: { ...columnModal.title, en: e.target.value } })} />
            </Field>
            <Field label="제목 (한국어)">
              <Input required value={columnModal.title.ko} onChange={(e) => setColumnModal({ ...columnModal, title: { ...columnModal.title, ko: e.target.value } })} />
            </Field>
            <Field label="শিরোনাম (বাংলা)">
              <Input required value={columnModal.title.bn} onChange={(e) => setColumnModal({ ...columnModal, title: { ...columnModal.title, bn: e.target.value } })} />
            </Field>
          </form>
        )}
      </Modal>

      {/* Social link modal */}
      <Modal
        open={!!socialModal}
        onClose={() => setSocialModal(null)}
        title={socialModal?.id ? 'Edit Social Link' : 'Add Social Link'}
        icon={<Instagram className="w-3.5 h-3.5" />}
        busy={busy}
        footer={
          <>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setSocialModal(null)} disabled={busy}>Cancel</Button>
            <Button type="submit" form="social-link-form" className="flex-1" loading={busy}>Save</Button>
          </>
        }
      >
        {socialModal && (
          <form id="social-link-form" onSubmit={saveSocialModal} className="space-y-3.5">
            <Field label="Platform">
              <Select value={socialModal.platform} onChange={(e) => setSocialModal({ ...socialModal, platform: e.target.value })}>
                {SOCIAL_PLATFORMS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </Select>
            </Field>
            <Field label="URL">
              <Input required type="url" placeholder="https://..." value={socialModal.url} onChange={(e) => setSocialModal({ ...socialModal, url: e.target.value })} />
            </Field>
          </form>
        )}
      </Modal>

      {/* Standalone text modal */}
      <Modal
        open={!!textModal}
        onClose={() => setTextModal(null)}
        title={textModal?.label ?? 'Edit Text'}
        icon={<Pencil className="w-3.5 h-3.5" />}
        busy={busy}
        footer={
          <>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setTextModal(null)} disabled={busy}>Cancel</Button>
            <Button type="submit" form="text-modal-form" className="flex-1" loading={busy}>Save</Button>
          </>
        }
      >
        {textModal && (
          <form id="text-modal-form" onSubmit={saveTextModal} className="space-y-3.5">
            <Field label="English">
              <Textarea required rows={3} value={textModal.en} onChange={(e) => setTextModal({ ...textModal, en: e.target.value })} />
            </Field>
            <Field label="한국어 (Korean)">
              <Textarea required rows={3} value={textModal.ko} onChange={(e) => setTextModal({ ...textModal, ko: e.target.value })} />
            </Field>
            <Field label="বাংলা (Bengali)">
              <Textarea required rows={3} value={textModal.bn} onChange={(e) => setTextModal({ ...textModal, bn: e.target.value })} />
            </Field>
          </form>
        )}
      </Modal>
    </AppShell>
  );
}
